import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { ShellSkeleton } from '../../shell/ShellSkeleton';
import type { HostInfo } from '../onboarding/admin/types';

/** `GET /api/public/host`: is this an organization host (customer domain, interim address) or the app? */
function useHost() {
  return useQuery({
    queryKey: ['public-host'],
    queryFn: () => api<HostInfo>('/public/host'),
    staleTime: Infinity,
    retry: false,
  });
}

/** The same path on the app domain (`appUrl` of the host response). */
function appLocation(appUrl: string, path: string): string {
  return new URL(path, appUrl.endsWith('/') ? appUrl : `${appUrl}/`).toString();
}

/**
 * `/` : on an organization host the client portal of that organization, on the app domain the start page.
 */
export function HostRoot() {
  const host = useHost();
  if (host.isPending) return <ShellSkeleton />;
  if (host.data?.kind === 'organization') {
    return <Navigate to={`/portal/${host.data.organizationSlug}`} replace />;
  }
  return <Navigate to="/home" replace />;
}

/** Paths an organization host serves itself: its portal, booking and meeting pages, public links, legal texts. */
const publicPrefixes = ['/portal/', '/book/', '/m/', '/r/', '/sd/', '/t/', '/u/', '/legal/'];

function isPublicPath(pathname: string): boolean {
  return pathname === '/' || publicPrefixes.some((prefix) => pathname.startsWith(prefix));
}

/**
 * The root layout of the router: on an organization host every internal path (sign-in, wizards, the app) moves to
 * the app domain, so nobody signs in to BRIMO on a customer's portal address. It renders at once and redirects when
 * the host answer arrives.
 */
export function HostGate() {
  const host = useHost();
  const location = useLocation();
  const target =
    host.data?.kind === 'organization' && !isPublicPath(location.pathname)
      ? appLocation(
          host.data.appUrl,
          `${location.pathname}${location.search}${location.hash}`.replace(/^\//, ''),
        )
      : null;
  useEffect(() => {
    if (target) window.location.replace(target);
  }, [target]);
  if (target) return <ShellSkeleton />;
  return <Outlet />;
}
